import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { useIsFetching } from '@tanstack/react-query';
import { numberLocale } from '@/lib/language';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePendingBandChange,
  useSetAddonMutation,
  useSetBandMutation,
} from '@/hooks/useManageSubscription';
import { getCurrency } from '@/lib/checkout';
import {
  isNoActiveSubscription,
  isSchedulePending,
  isSpecialPlanConflict,
  isSubscriptionNotManageable,
  notManageableStatus,
} from '@/lib/manageSubscription';
import {
  PLANS,
  SUPPORT_EMAIL,
  addonPriceLabel,
  formatPlanPrice,
  hasPaidSubscription,
  isCapTier,
  lookupForCycle,
  planDisplayName,
  type PlanInfo,
} from '@/lib/plans';
import type { Subscription } from '@/hooks/useSubscription';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BandChangeConfirmDialog } from '@/components/pricing/BandChangeConfirmDialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Languages, Minus, Plus, Store, X } from 'lucide-react';

/**
 * Inklusive Merchant Center bzw. Zielsprachen je Bandgroesse.
 * Quelle: `supabase/functions/_shared/plan-features.ts` — die Durchsetzung
 * lebt dort; hier bewusst schlank fuers Anzeigen dupliziert (1 bis zum
 * 2.000er-Band inkl. Free und Legacy-Kleintarife, 3 fuer 5.000/10.000,
 * unbegrenzt ab 25.000).
 */
export function includedAllowance(productLimit: number | null | undefined): number | 'unlimited' {
  const limit = productLimit ?? 200;
  if (limit >= 25_000) return 'unlimited';
  if (limit >= 5_000) return 3;
  return 1;
}

type AddonKind = 'mc' | 'language';

interface PlanBandManagerProps {
  subscription: Subscription | null | undefined;
}

type PendingAddon = {
  addon: AddonKind;
  nextQuantity: number;
  /** Reduktionen brauchen den Erstattungs-Hinweis (keine anteilige Gutschrift). */
  isDecrease: boolean;
};

/**
 * Settings → Abonnement: Add-on-Zeilen (Merchant Center / Zielsprachen) und
 * — fuer Bestandskunden mit Stripe-Abo — der Bandwechsel-Abschnitt. Beide
 * Aktionen laufen ueber die Edge Function `manage-subscription`; ohne aktives
 * Abo antwortet die mit 409 und die Karte verweist auf die Preisseite
 * ("erst Band waehlen").
 */
export const PlanBandManager: React.FC<PlanBandManagerProps> = ({ subscription }) => {
  const { t, i18n } = useTranslation('settings');
  const { user } = useAuth();
  const setAddonMutation = useSetAddonMutation();
  const setBandMutation = useSetBandMutation();
  const pendingChange = usePendingBandChange();

  const [pendingAddon, setPendingAddon] = useState<PendingAddon | null>(null);
  const [selectedBandTier, setSelectedBandTier] = useState<string | null>(null);
  const [bandConfirmOpen, setBandConfirmOpen] = useState(false);
  /** 409 NO_ACTIVE_SUBSCRIPTION → Hinweis "erst Band waehlen" + /pricing-Link. */
  const [needsBandFirst, setNeedsBandFirst] = useState(false);
  /**
   * 409 SCHEDULE_PENDING → Box mit Erklaerung + "Wechsel aufheben"-Knopf.
   * Deckt insbesondere den Reload-Fall ab, in dem der session-lokale
   * `pendingChange`-Merker weg ist, der Schedule bei Stripe aber noch steht.
   */
  const [schedulePendingBlocked, setSchedulePendingBlocked] = useState(false);

  const nf = (n: number) => n.toLocaleString(numberLocale(i18n.language));
  const currency = getCurrency();
  const addonPrice = addonPriceLabel(currency, i18n.language);

  const included = includedAllowance(subscription?.product_limit);
  const mcBooked = subscription?.addon_mc_booked ?? 0;
  const langBooked = subscription?.addon_language_booked ?? 0;

  const hasPaidPlan = hasPaidSubscription(subscription);

  const capPlans = PLANS.filter((p) => isCapTier(p.tier));
  const currentTier = subscription?.plan_tier ?? 'free';
  /**
   * MINOR-4 (bewusste Grenze): der Bandwechsel behaelt IMMER das aktuelle
   * Abrechnungsintervall bei — ein monthly↔yearly-Wechsel in-App ist kein
   * Feature dieser Karte. Wer das Intervall wechseln will, geht ueber das
   * Stripe-Kundenportal bzw. den Support.
   *
   * Zum Yearly-Default fuer Neuabschluesse (CRO 21.08.2026): diese Karte
   * bedient den ERSTKAUF nicht — der Bandwechsel-Abschnitt rendert nur bei
   * `hasPaidPlan`, und ohne lebendes Abo antwortet `manage-subscription` mit
   * 409 NO_ACTIVE_SUBSCRIPTION → Hinweis-Box mit /pricing-Link, wo der
   * Neuabschluss-Default (yearly) greift. Der Monthly-Fallback hier betrifft
   * also ausschliesslich zahlende Bestandskunden ohne `billing_interval =
   * 'yearly'` und bleibt bewusst monthly.
   */
  const billingCycle: 'monthly' | 'yearly' =
    subscription?.billing_interval === 'yearly' ? 'yearly' : 'monthly';
  const lookupFor = (p: PlanInfo) => lookupForCycle(p, billingCycle);

  /**
   * Das aktuelle Band als Ziel des Abbruch-Pfads (setBand aufs AKTUELLE Band
   * released den pendenden Schedule serverseitig).
   *
   * MINOR-5 (bewusste Grenze): Legacy-Tiers (starter/business/…/enterprise)
   * haben keinen `cap*`-Lookup-Key und die Server-Allowlist kennt nur Baender —
   * fuer sie gibt es deshalb keinen In-App-Abbruch-Knopf. Ein Legacy-Kunde mit
   * pendendem Downgrade wartet auf den Stichtag oder meldet sich beim Support.
   */
  const currentPlan = capPlans.find((p) => p.tier === currentTier) ?? null;

  const bandName = (p: PlanInfo) => planDisplayName(p, i18n?.language, t);
  const selectedBand = capPlans.find((p) => p.tier === selectedBandTier) ?? null;
  const pendingPlan = pendingChange
    ? capPlans.find((p) => pendingChange.targetLookup.startsWith(`${p.tier}_`)) ?? null
    : null;

  /**
   * MINOR-2 (Stale-booked-Race): nach einer Add-on-Aenderung invalidiert der
   * Hook die Subscription-Query; bis deren Refetch settled zeigt `booked` noch
   * den alten Wert. Ein Klick in dieser Luecke wuerde eine falsche absolute
   * Menge senden — die Knoepfe bleiben deshalb disabled, solange die Query
   * noch faehrt ODER die Mutation laeuft.
   */
  const subscriptionRefreshing = useIsFetching({ queryKey: ['subscription', user?.id] }) > 0;
  const addonBusy = setAddonMutation.isPending || subscriptionRefreshing;

  const requestAddonChange = (addon: AddonKind, current: number, delta: 1 | -1) => {
    const nextQuantity = Math.max(0, current + delta);
    if (nextQuantity === current) return;
    setPendingAddon({ addon, nextQuantity, isDecrease: delta < 0 });
  };

  /** MAJOR-1: alle ueberfluessigen Add-ons auf einmal entfernen (Menge 0). */
  const requestAddonRemoval = (addon: AddonKind, current: number) => {
    if (current === 0) return;
    setPendingAddon({ addon, nextQuantity: 0, isDecrease: true });
  };

  /**
   * Gemeinsame Fehlerbehandlung beider Aktionen. Die drei erwarteten 409er
   * (kein Abo / pendender Schedule / nicht verwaltbarer Stripe-Status) sind
   * Zustaende, keine Defekte — sie bekommen spezifische Meldungen und KEIN
   * `Sentry.captureException` (MAJOR-2). Nur der generische Rest wird gemeldet.
   */
  const handleManageError = (error: unknown, action: 'set-addon' | 'set-band') => {
    if (isNoActiveSubscription(error)) {
      setNeedsBandFirst(true);
      return;
    }
    if (isSchedulePending(error)) {
      setSchedulePendingBlocked(true);
      return;
    }
    if (isSubscriptionNotManageable(error)) {
      toast.error(t('planBand.notManageable', { status: notManageableStatus(error) }));
      return;
    }
    if (isSpecialPlanConflict(error)) {
      toast.error(t('checkout:errors.specialPlan', { email: SUPPORT_EMAIL }));
      return;
    }
    Sentry.captureException(error, { tags: { feature: 'billing', action } });
    toast.error(t('planBand.actionFailed'));
  };

  const confirmAddonChange = async () => {
    if (!pendingAddon) return;
    const { addon, nextQuantity } = pendingAddon;
    setPendingAddon(null);
    try {
      await setAddonMutation.mutateAsync({ addon, quantity: nextQuantity });
      setNeedsBandFirst(false);
      toast.success(t('planBand.addonUpdated'));
    } catch (error) {
      handleManageError(error, 'set-addon');
    }
  };

  const confirmBandChange = async () => {
    if (!selectedBand) return;
    setBandConfirmOpen(false);
    try {
      const result = await setBandMutation.mutateAsync(lookupFor(selectedBand));
      setNeedsBandFirst(false);
      setSchedulePendingBlocked(false);
      if (result.status === 'scheduled') {
        toast.success(t('planBand.bandScheduled', {
          date: result.effective_at
            ? new Date(result.effective_at).toLocaleDateString(numberLocale(i18n.language))
            : t('planBand.periodEnd'),
        }));
      } else if (result.status === 'released') {
        toast.success(t('planBand.scheduleReleased'));
      } else {
        toast.success(t('planBand.bandUpdated', { plan: bandName(selectedBand) }));
      }
    } catch (error) {
      handleManageError(error, 'set-band');
    }
  };

  /**
   * MAJOR-3: Abbruch eines geplanten Bandwechsels — setBand aufs AKTUELLE
   * Band, der Server released den pendenden Schedule. Ruft die Mutation
   * DIREKT auf (nicht ueber die Band-Auswahl, deren aktueller Eintrag
   * disabled ist).
   */
  const cancelPendingBandChange = async () => {
    if (!currentPlan) return;
    try {
      const result = await setBandMutation.mutateAsync(lookupFor(currentPlan));
      setSchedulePendingBlocked(false);
      setNeedsBandFirst(false);
      // 'released' ist die erwartete Antwort; 'updated' heisst, es stand gar
      // kein Schedule mehr — das Ziel (kein geplanter Wechsel) ist in beiden
      // Faellen erreicht.
      toast.success(t('planBand.scheduleReleased'));
    } catch (error) {
      handleManageError(error, 'set-band');
    }
  };

  const addonRow = (
    addon: AddonKind,
    icon: React.ReactNode,
    label: string,
    booked: number,
  ) => {
    // MAJOR-1: "unbegrenzt inklusive" darf gebuchte Add-ons nicht verstecken —
    // die kosten weiter Geld, obwohl das Band sie ueberfluessig macht. Deshalb
    // Hinweis + Verringern-/Entfernen-Knoepfe, solange booked > 0.
    const redundantBooked = included === 'unlimited' && booked > 0;
    return (
      <div className="flex items-center justify-between gap-3" data-testid={`addon-row-${addon}`}>
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <div className="min-w-0">
            <p className="text-sm font-medium">{label}</p>
            {included === 'unlimited' ? (
              redundantBooked ? (
                <p className="text-xs text-warning-ink">
                  {t('planBand.includedUnlimitedBooked', { count: booked })}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">{t('planBand.includedUnlimited')}</p>
              )
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('planBand.includedBooked', { included: nf(included), booked: nf(booked) })}
              </p>
            )}
          </div>
        </div>
        {(included !== 'unlimited' || redundantBooked) && (
          <div className="flex items-center gap-1 shrink-0">
            {booked > 0 ? (
              <>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  aria-label={t('planBand.decrease', { label })}
                  disabled={addonBusy}
                  onClick={() => requestAddonChange(addon, booked, -1)}
                >
                  <Minus className="h-3.5 w-3.5" />
                </Button>
                {redundantBooked ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    aria-label={t('planBand.removeAll', { label })}
                    disabled={addonBusy}
                    onClick={() => requestAddonRemoval(addon, booked)}
                  >
                    <X className="h-3.5 w-3.5" />
                    {t('planBand.remove')}
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-7 w-7"
                    aria-label={t('planBand.increase', { label })}
                    disabled={addonBusy}
                    onClick={() => requestAddonChange(addon, booked, 1)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                )}
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                disabled={addonBusy}
                onClick={() => requestAddonChange(addon, booked, 1)}
              >
                <Plus className="h-3.5 w-3.5" />
                {t('planBand.book')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Separator />

      {/* Add-ons */}
      <div className="space-y-3">
        <p className="text-sm font-semibold">{t('planBand.addonsTitle')}</p>
        {addonRow('mc', <Store className="h-4 w-4 text-muted-foreground shrink-0" />, t('planBand.mcLabel'), mcBooked)}
        {addonRow('language', <Languages className="h-4 w-4 text-muted-foreground shrink-0" />, t('planBand.languageLabel'), langBooked)}
        <p className="text-xs text-muted-foreground">
          {t('planBand.addonPriceNote', { price: addonPrice })}
        </p>
      </div>

      {needsBandFirst && (
        <div className="rounded-md border border-warning/30 bg-warning-light p-3 text-sm text-warning-ink">
          {t('planBand.needsBandFirst')}{' '}
          <Link to="/pricing" className="underline font-medium">
            {t('planBand.needsBandFirstLink')}
          </Link>
        </div>
      )}

      {/* 409 SCHEDULE_PENDING: Erklaerung + direkter Abbruch-Pfad (MAJOR-3). */}
      {schedulePendingBlocked && (
        <div
          className="rounded-md border border-warning/30 bg-warning-light p-3 text-sm text-warning-ink space-y-2"
          data-testid="schedule-pending-box"
        >
          <p>{t('planBand.schedulePending')}</p>
          {currentPlan && (
            <Button
              size="sm"
              variant="outline"
              disabled={setBandMutation.isPending}
              onClick={cancelPendingBandChange}
            >
              {t('planBand.cancelPendingChange')}
            </Button>
          )}
        </div>
      )}

      {/* Bandwechsel (nur Bestandskunden mit Stripe-Abo; Neukunden gehen ueber
          die Preisseite in den Checkout). */}
      {hasPaidPlan && (
        <div className="space-y-2">
          <Separator />
          <p className="text-sm font-semibold">{t('planBand.changeBandTitle')}</p>

          {/* Session-lokal gemerkter, geplanter Wechsel (MAJOR-3) — nach einem
              Reload uebernimmt die SCHEDULE_PENDING-Box oben. */}
          {pendingChange && (
            <div
              className="rounded-md border bg-muted/50 p-3 text-sm flex flex-wrap items-center justify-between gap-2"
              data-testid="pending-band-change"
            >
              <span>
                {t('planBand.pendingChange', {
                  plan: pendingPlan ? bandName(pendingPlan) : pendingChange.targetLookup,
                  date: pendingChange.effectiveAt
                    ? new Date(pendingChange.effectiveAt).toLocaleDateString(numberLocale(i18n.language))
                    : t('planBand.periodEnd'),
                })}
              </span>
              {currentPlan && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={setBandMutation.isPending}
                  onClick={cancelPendingBandChange}
                >
                  {t('planBand.cancelPendingChange')}
                </Button>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={selectedBandTier ?? undefined}
              onValueChange={(v) => setSelectedBandTier(v)}
            >
              <SelectTrigger className="w-56" aria-label={t('planBand.changeBandTitle')}>
                <SelectValue placeholder={t('planBand.selectBand')} />
              </SelectTrigger>
              <SelectContent>
                {capPlans.map((p) => (
                  <SelectItem key={p.tier} value={p.tier} disabled={p.tier === currentTier}>
                    {bandName(p)} · {formatPlanPrice(p, currency, 'monthly', i18n.language)}{t('planBand.perMonth')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              disabled={!selectedBand || selectedBand.tier === currentTier || setBandMutation.isPending}
              onClick={() => setBandConfirmOpen(true)}
            >
              {t('planBand.changeBandCta')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t('planBand.downgradeNote')}</p>
        </div>
      )}

      {/* Bestaetigung Add-on */}
      <AlertDialog open={!!pendingAddon} onOpenChange={(v) => !v && setPendingAddon(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {/* Der Titel nennt das Add-on (UX-Audit SEA-A6-12): vorher hiess
                  er fuer Merchant Center und Zielsprache gleich. */}
              {pendingAddon && t(
                pendingAddon.isDecrease
                  ? 'planBand.confirmAddonDecreaseTitleNamed'
                  : 'planBand.confirmAddonTitleNamed',
                { addon: t(`planBand.addonNames.${pendingAddon.addon}`) },
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAddon && (
                pendingAddon.isDecrease
                  ? t('planBand.confirmAddonDecreaseBody', { quantity: nf(pendingAddon.nextQuantity) })
                  : t('planBand.confirmAddonBody', { price: addonPrice, quantity: nf(pendingAddon.nextQuantity) })
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('planBand.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAddonChange}>
              {t('planBand.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bestaetigung Bandwechsel: der geteilte Dialog (UX-Audit K36) nennt
          Richtung, alten und neuen Preis. Das Intervall bleibt beim Wechsel
          in dieser Karte gleich, deshalb kein eigenes fromCycle. */}
      <BandChangeConfirmDialog
        open={bandConfirmOpen}
        from={currentPlan}
        to={selectedBand}
        cycle={billingCycle}
        currency={currency}
        onConfirm={confirmBandChange}
        onCancel={() => setBandConfirmOpen(false)}
      />
    </div>
  );
};
